import moment from "moment";

import { MyleTableColumn } from "@/Common/Components/Tables/MyleTableColumn";
import { DocumentTreeClassControl } from "@/Common/Helpers/DocumentTreeClassControl";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { AttachedCommunication } from "@/Common/Models/AttachedCommunication/AttachedCommunication";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { SharedItemPriority } from "@/Common/Models/Portal/SharedItem/SharedItemPriority";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { DocumentSortOrderType, RedirectTo, SortDirection } from "@/Common/Models/UI/Enums";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { CheckboxUIModel } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { MedicalDocumentItem } from "@/Documents/Models/Medical/MedicalDocumentItem";
import { DocumentSearchSettings } from "@/Documents/Models/Settings/DocumentSearchSettings";
import { IDocumentsTabModel } from "@/Documents/Models/UI/IDocumentsTabModel";
import { DocumentsStore } from "@/Documents/Stores/DocumentsStore";

class DocumentSearchHandler extends BaseOperationHandler {
    private readonly helper: MyleHelper;
    private throttler: number | null;
    private throttleTimeMilliseconds: number;

    constructor() {
        super();

        this.throttleTimeMilliseconds = 650;
        this.helper = new MyleHelper();

        this.RegisterOperation(LocalOperations.Documents.SearchSettings_ShowOptionsButtonClick, this.toggleSearchOptionsDropdown);
        this.RegisterOperation(LocalOperations.Misc.Table_ColumnSortClick, this.setColumnSort);
        this.RegisterOperation(LocalOperations.Documents.Search_ChangePage, this.searchResultsChangePage);
        this.RegisterOperation(LocalOperations.Documents.SearchSettings_OnRefreshClick, this.searchDocumentsAndResetPaging);
        this.RegisterOperation(LocalOperations.Documents.SearchSettings_OnOkClick, this.searchDocumentsAndResetPaging);
        this.RegisterOperation(LocalOperations.Documents.SearchSettings_SearchBoxTextChanged, this.onDocumentSearchStringChange);
        this.RegisterOperation(LocalOperations.Documents.Checkbox_Change, this.onCheckboxCheck);
        this.RegisterOperation(LocalOperations.Documents.SearchSettings_ClinicianFilterTextChanged, this.clinicianFilterChanged);
        this.RegisterOperation(LocalOperations.Documents.SearchSettings_SentByFilterTextChanged, this.sentByFilterChanged);
        this.RegisterOperation(LocalOperations.Documents.SearchSettings_FromDateChanged, this.fromDateChanged);
        this.RegisterOperation(LocalOperations.Documents.SearchSettings_ToDateChanged, this.toDateChanged);
        this.RegisterOperation(LocalOperations.Documents.SearchSettings_OnCancelClick, this.onCancelClick);
        this.RegisterOperation(LocalOperations.Documents.SearchSettings_OnResultsPerPageChange, this.onResultsPerPageChange);
        this.RegisterOperation(LocalOperations.Misc.MyleApp_Container_Click, this.onContainerClick, false);

        this.RegisterOperation(LocalOperations.Documents.PortalStatusClicked, this.onPortalStatusClicked);
        this.RegisterOperation(LocalOperations.Documents.PortalStatusCancel, this.onPortalStatusCancel);
        this.RegisterOperation(LocalOperations.Documents.PortalSendClicked, this.onPortalSendClicked);
        this.RegisterOperation(LocalOperations.Documents.PortalDocumentNoteMessageChecked, this.onPortalDocumentNoteMessageChecked);
        this.RegisterOperation(LocalOperations.Documents.PortalRevokedClicked, this.onPortalRevokedClicked);
        this.RegisterOperation(LocalOperations.Documents.Search_TrailClick, this.trailClick);
        this.RegisterOperation(LocalOperations.Documents.Search_AttachedCommunicationClick, this.attachedCommunicationClick);
        this.RegisterOperation(LocalOperations.Documents.Search_AttachedCommunicationClose, this.attachedCommunicationClose);
        this.RegisterOperation(LocalOperations.Documents.SearchSettings_UpdateDocumentClassLocalizedNames, this.updateDocumentClassLocalizedNames);
        this.RegisterOperation(LocalOperations.Documents.Show_OnlySelectedSites, this.showOnlySelectedSites);

        this.RegisterOperation(LocalOperations.Documents.ResetCache, this.resetCache);

        // HRM_CLEANUP: Delete refreshDocumentClasses registration when Ontario MD certification is complete.
        this.RegisterOperation(LocalOperations.Documents.RefreshDocumentClasses, this.refreshDocumentClasses);
    }

    private onDocumentSearchStringChange(request: LocalEvent, store: DocumentsStore): void {
        let value = request.Get<string>("value");
        store.DocumentsModel = store.DocumentsModel.set("GlobalDocumentSearchText", value);

        if (this.throttler != null) { clearTimeout(this.throttler); this.throttler = null; }
        let d = this;
        this.throttler = window.setTimeout(
            () => {
                store.DocumentsModel = store.DocumentsModel.set("IsLoadingFromServer", true);
                d.searchDocumentsAndResetPaging(request, store);
            },
            d.throttleTimeMilliseconds
        );
    }

    private toggleSearchOptionsDropdown(request: LocalEvent, store: DocumentsStore): void {
        let open = store.DocumentsModel.get("SearchOptionsOpen");
        store.DocumentsModel = store.DocumentsModel.set("SearchOptionsOpen", !open);

        if (store.DocumentsModel.get("SearchOptionsOpen")) {
            let isfromPatient = store.DocumentsModel.get("IsFromPatient");
            store.SetDefaultSearchFilters(isfromPatient);
        }
        else {
            window.setTimeout(
                () => {
                    let operation = new LocalEvent(LocalOperations.Documents.SearchSettings_OnRefreshClick);
                    LocalEventDispatcher.dispatch(operation);
                },
                5
            );
        }
    }

    public searchDocumentsAndResetPaging(request: LocalEvent, store: DocumentsStore): void {
        const isFromPatient = store.DocumentsModel.get("IsFromPatient");

        if (store.DocumentsModel.get("SaveDisplaySettings") == true) {
            store.DocumentActions.SaveDocumentDisplaySetting(DocumentSearchSettings.FromDocTabModel(store.DocumentsModel), isFromPatient);
        }
        store.DocumentsModel = store.DocumentsModel.set("DocumentsTableData", Immutable.List([]));
        store.DocumentsModel = store.DocumentsModel.set("TotalQueryPages", 0);
        store.DocumentsModel = store.DocumentsModel.set("TotalQueryRows", 0);
        store.DocumentsModel = store.DocumentsModel.set("CurrentPage", 1);
        store.DocumentsModel = store.DocumentsModel.set("IsLoadingFromServer", true);

        if (store.DocumentsModel.get("SelectedView") === "deactivatedAccount")
            store.DocumentsModel = store.DocumentsModel.set("DeactivatedAccountCliniciansCache", store.getDefaultGlobalCliniciansCache(true));

        store.DocumentActions.SearchDocuments(store.DocumentsModel, true);
    }

    public setColumnOrder(request: LocalEvent, store: DocumentsStore): void {
        let tableTarget = request.Get<MyleTableTarget>("target");
        if (tableTarget === MyleTableTarget.Myle_Documents_SearchResults_ColumnReorder) {
            let order = request.Get<string[]>("columnOrder");
            store.DocumentsModel = store.DocumentsModel.set("SearchResultsColumnOrder", Immutable.List(order));
        }
    }

    public setColumnSort(request: LocalEvent, store: DocumentsStore): void {
        let tableTarget = request.Get<MyleTableTarget>("target");

        if (tableTarget === MyleTableTarget.Myle_Documents_SearchResults_ColumnSortClick) {
            let columnOrderType = request.Get<DocumentSortOrderType>("sortColumn");

            if (store.DocumentsModel.get("SearchResultsSortColumn") && store.DocumentsModel.get("SearchResultsSortColumn") === columnOrderType) {
                if (store.DocumentsModel.get("SearchResultsSortColumnDirection") === SortDirection.ASC)
                    store.DocumentsModel = store.DocumentsModel.set("SearchResultsSortColumnDirection", SortDirection.DESC);
                else
                    store.DocumentsModel = store.DocumentsModel.set("SearchResultsSortColumnDirection", SortDirection.ASC);
            }
            else {
                store.DocumentsModel = store.DocumentsModel.set("SearchResultsSortColumn", columnOrderType);
                store.DocumentsModel = store.DocumentsModel.set("SearchResultsSortColumnDirection", SortDirection.DESC);
            }

            let column = request.Get<MyleTableColumn>("columnObject");

            if (column) {
                if (column.columnOptions.IsSortActive) {
                    column.ToggleSortDirection();
                }
                column.columnOptions.IsSortActive = true;

                store.DocumentsModel = store.DocumentsModel.set("UpdatedTableColumn", column);
            }

            store.DocumentsModel = store.DocumentsModel.set("IsLoadingFromServer", true);
            store.DocumentActions.SearchDocuments(store.DocumentsModel, true);
        }
    }

    public searchResultsChangePage(request: LocalEvent, store: DocumentsStore): void {
        let page = request.Get<number>("Page");
        store.DocumentsModel = store.DocumentsModel.set("SelectedPageNumber", page);
        if (store.DocumentsModel.get("SelectedView") === "orphan") {
            store.DocumentActions.SearchDocuments(store.DocumentsModel);
        }
        else {
            store.DocumentsModel = store.DocumentsModel.set("IsLoadingFromServer", true);
            store.DocumentActions.SearchDocuments(store.DocumentsModel, false);
        }
    }

    private onCheckboxCheck(request: LocalEvent, store: DocumentsStore): void {
        let target = request.Get<CheckboxTarget>("target");
        let checked = request.Get<boolean>("checked");
        let id = request.Get<string>("id");

        switch (target) {
            case CheckboxTarget.Myle_Documents_Sites:
                let current = store.getCliniciansCache();
                store.DocumentsModel = store.DocumentsModel.set("Sites", this.helper.FindUpdateICheckableItem<SiteUIModel>(store.DocumentsModel.get("Sites"), id, checked));
                store.setCliniciansCache(store.UpdateVisibilityToCliniciansFromSelectedSites(current));
                break;
            case CheckboxTarget.Myle_Documents_Clinicians:
                let currentLists = store.getCliniciansCache();
                let updatedLists = currentLists.map(c => { if (c.Id === id) { c.Checked = checked; } return c; }).toList();
                store.setCliniciansCache(updatedLists);
                break;
            case CheckboxTarget.Myle_Documents_Status:
                store.DocumentsModel = store.DocumentsModel.set("Status", this.helper.FindUpdateICheckableItem<CheckboxUIModel>(store.DocumentsModel.get("Status"), id, checked));
                break;
            case CheckboxTarget.Myle_Documents_Classes:
                store.DocumentsModel = store.DocumentsModel.set("Classes", this.helper.FindUpdateICheckableItem<CheckboxUIModel>(store.DocumentsModel.get("Classes"), id, checked));
                break;
            case CheckboxTarget.Myle_Documents_Urgency:
                store.DocumentsModel = store.DocumentsModel.set("Urgency", this.helper.FindUpdateICheckableItem<CheckboxUIModel>(store.DocumentsModel.get("Urgency"), id, checked));
                break;
            case CheckboxTarget.Myle_Documents_SitesControlCheck:
                let _currentState = store.getCliniciansCache();
                store.DocumentsModel = store.DocumentsModel.set("SelectAllSitesChecked", checked);
                store.DocumentsModel = store.DocumentsModel.set("ShowOnlySelectedSites", false);
                let sites = this.helper.ChangeCheckStateFromAllICheckable(store.DocumentsModel.get("Sites"), checked);
                store.DocumentsModel = store.DocumentsModel.set("Sites", sites);
                store.setCliniciansCache(store.UpdateVisibilityToCliniciansFromSelectedSites(_currentState));
                break;
            case CheckboxTarget.Myle_Documents_CliniciansControlCheck:
                store.DocumentsModel = store.DocumentsModel.set("SelectAllCliniciansChecked", checked);
                let lsts = store.getCliniciansCache();
                lsts = lsts.map(c => { if (c.Visible) { c.Checked = checked; } return c; }).toList();
                store.setCliniciansCache(lsts);
                break;
            case CheckboxTarget.Myle_Documents_SentByControlCheck:
                store.DocumentsModel = store.DocumentsModel.set("SelectAllSentByChecked", checked);
                let lst = store.DocumentsModel.get("SentByGlobalCache");
                lst = lst.map(c => { if (c.Visible) { c.Checked = checked; } return c; }).toList();
                store.DocumentsModel = store.DocumentsModel.set("SentByGlobalCache", lst);
                break;
            case CheckboxTarget.Myle_Documents_ClassControlCheck:
                store.DocumentsModel = store.DocumentsModel.set("SelectAllClassesChecked", checked);
                store.DocumentsModel = store.DocumentsModel.set("Classes", this.helper.ChangeCheckStateFromAllICheckable(store.DocumentsModel.get("Classes"), checked));
                break;
            case CheckboxTarget.Myle_Documents_StatusControlCheck:
                store.DocumentsModel = store.DocumentsModel.set("SelectAllStatusChecked", checked);
                store.DocumentsModel = store.DocumentsModel.set("Status", this.helper.ChangeCheckStateFromAllICheckable(store.DocumentsModel.get("Status"), checked));
                break;
            case CheckboxTarget.Myle_Documents_UrgencyControlCheck:
                store.DocumentsModel = store.DocumentsModel.set("SelectAllUrgencyChecked", checked);
                store.DocumentsModel = store.DocumentsModel.set("Urgency", this.helper.ChangeCheckStateFromAllICheckable(store.DocumentsModel.get("Urgency"), checked));
                break;
            case CheckboxTarget.Myle_Documents_PortalStatusControlCheck:
                store.DocumentsModel = store.DocumentsModel.set("SelectAllPortalStatus", checked);
                store.DocumentsModel = store.DocumentsModel.set("PortalStatuses", this.helper.ChangeCheckStateFromAllICheckable(store.DocumentsModel.get("PortalStatuses"), checked));
                break;
            case CheckboxTarget.Myle_Documents_PortalStatus:
                store.DocumentsModel = store.DocumentsModel.set("PortalStatuses", this.helper.FindUpdateICheckableItem<CheckboxUIModel>(store.DocumentsModel.get("PortalStatuses"), id, checked));
                break;
            case CheckboxTarget.Myle_Documents_SentBy:
                let currentList = store.DocumentsModel.get("SentByGlobalCache");
                let updatedList = currentList.map(c => { if (c.Id === id) { c.Checked = checked; } return c; }).toList();
                store.DocumentsModel = store.DocumentsModel.set("SentByGlobalCache", updatedList);
                break;
            case CheckboxTarget.Myle_Documents_OnlySelectedSites:
                store.DocumentsModel = store.DocumentsModel.set("ShowOnlySelectedSites", checked);
                break;
            case CheckboxTarget.Myle_Documents_SaveSettingsCheck:
                store.DocumentsModel = store.DocumentsModel.set("SaveDisplaySettings", checked);
                break;

            default:
                break;
        }
    }

    private updateDocumentClassLocalizedNames(request: LocalEvent, store: DocumentsStore): void {
        const currentClasses = store.DocumentsModel?.get("Classes")?.toArray();
        const updatedClasses = DocumentTreeClassControl.GetDocumentClasses().toArray();
        if (currentClasses?.any()) {
            currentClasses.forEach(currentClass => {
                const matchedClass = updatedClasses.firstOrDefault(updatedClass => updatedClass.LookupId === currentClass.Id);
                if (matchedClass)
                    currentClass.Name = matchedClass.Name;
            });
            store.DocumentsModel?.set("Classes", currentClasses.toImmutable());
        }
    }

    private clinicianFilterChanged(request: LocalEvent, store: DocumentsStore): void {
        store.DocumentsModel = store.DocumentsModel.set("CliniciansSearchText", request.Get<string>("value"));
    }

    private sentByFilterChanged(request: LocalEvent, store: DocumentsStore): void {
        store.DocumentsModel = store.DocumentsModel.set("SentBySearchText", request.Get<string>("value"));
    }

    private fromDateChanged(request: LocalEvent, store: DocumentsStore): void {
        const nextFromDate = request.Get<Date>("value");
        const doc = store.DocumentsModel;
        store.DocumentsModel = store.DocumentsModel
            .set("DateFrom", nextFromDate)
            .set("ValidationError", this.getValidationError(nextFromDate, doc.get("DateTo"), doc.get("IsFromPatient")));
    }

    private toDateChanged(request: LocalEvent, store: DocumentsStore): void {
        const nextToDate = request.Get<Date>("value");
        const doc = store.DocumentsModel;
        store.DocumentsModel = store.DocumentsModel
            .set("DateTo", request.Get<Date>("value"))
            .set("ValidationError", this.getValidationError(doc.get("DateFrom"), nextToDate, doc.get("IsFromPatient")));
    }

    private getValidationError(fromDate: Date, toDate: Date, isFromPatient: boolean): string | null {
        if (!!fromDate && !!toDate && (fromDate > toDate))
            return cacheStore.GetString("StartDateCannotBeGreaterThanEndDateLabel");

        if (!!isFromPatient)
            return null;

        return ValidationHelper.validateSearchDateRangeMax5Years(fromDate, toDate);
    }

    private onCancelClick(request: LocalEvent, store: DocumentsStore): void {
        const initialState = request.Get<IDocumentsTabModel>("initialState");
        store.DocumentsModel = initialState.set("SearchOptionsOpen", false);
    }

    private onResultsPerPageChange(request: LocalEvent, store: DocumentsStore): void {
        store.DocumentsModel = store.DocumentsModel.set("SelectedPageSizeOption", request.Get<number>("value"));
        store.DocumentsModel = store.DocumentsModel.set("SelectedPageNumber", 1);
    }

    private onContainerClick(request: LocalEvent, store: DocumentsStore): void {
        if (store.DocumentsModel.get("SearchOptionsOpen") && !store.DocumentsModel.get("ValidationError")) {
            store.DocumentsModel = store.DocumentsModel.set("SearchOptionsOpen", false);
            store.DocumentsModel = store.DocumentsModel.set("IsLoadingFromServer", true);
            store.DocumentActions.SearchDocuments(store.DocumentsModel, true);
            store.EmitChangeToListeners();
        }

        if (store.DocumentsModel.get("ColumnOrderingPopupOpen")) {
            store.EmitChangeToListeners();
        }

        if (store.DocumentsModel.get("AttachedCommunicationsPopupOpen"))
            store.DocumentsModel = store.DocumentsModel.set("AttachedCommunicationsPopupOpen", false);
    }

    private onPortalStatusClicked(request: LocalEvent, store: DocumentsStore): void {
        let doc = request.Get<MedicalDocumentItem>("doc");
        let docModel = doc.Model;

        if (docModel != null) {
            store.transformDocumentToSharedItems(doc);
            store.DocumentsModel = store.DocumentsModel.set("IsSendToPatientPortalPopupOpen", true);

            sessionStore.showGlobalOverlay(true);
        }
    }

    private onPortalStatusCancel(request: LocalEvent, store: DocumentsStore): void {
        let docSyncModel = store.DocumentsModel.get("PortalLinkedItemModel");
        docSyncModel = PatientHelper.clearPortalLinkItemStates(docSyncModel);
        store.DocumentsModel = store.DocumentsModel.set("PortalLinkedItemModel", docSyncModel);

        store.DocumentsModel = store.DocumentsModel.set("IsSendToPatientPortalPopupOpen", false);
        $("table.myle-table tbody tr").removeClass("active");

        sessionStore.hideGlobalOverlay();
    }

    private onPortalDocumentNoteMessageChecked(request: LocalEvent, store: DocumentsStore): void {
        let checkedItem = request.Get<SharedItemMessage>("item");

        if (checkedItem != null) {
            let model = store.DocumentsModel.get("PortalLinkedItemModel");
            model = PatientHelper.handlePortalItemCheck(model, checkedItem);
            store.DocumentsModel = store.DocumentsModel.set("PortalLinkedItemModel", model);
        }
    }

    private onPortalSendClicked(request: LocalEvent, store: DocumentsStore): void {
        const subject = request.Get<string>("subject");
        const messagetext = request.Get<string>("messageText");
        const includeDocument = request.Get<boolean>("includeDocument");
        const priority = request.Get<SharedItemPriority>("priority");
        const canPatientReplyWithMessage = request.Get<boolean>("canPatientReplyWithMessage");

        let docSyncModel = store.DocumentsModel.get("PortalLinkedItemModel");
        const objectId: string = !IsNullOrWhiteSpace(docSyncModel.get("LinkedItemId")) ? docSyncModel.get("LinkedItemId") : "";
        const doc = docSyncModel.get("LinkedItemModel") as MedicalDocumentItem;
        const docModel = doc.Model;

        let portalMessages: SharedItemMessage[] = [];
        if (docSyncModel != null) {
            portalMessages = PatientHelper.getShareItemMessagesToRevokeOrToSendToPortal(docSyncModel);
        }

        if (docModel.IsAnnotated && docModel.DocumentPathList?.any() && docSyncModel) {
            for (const path of docModel.DocumentPathList)
                store.DocumentActions.copyAnnotatedDocumentToPortal(path);
        }

        const sharedItemId: string = docSyncModel.get("LinkedItemModel").getSharedItemId();

        const hasTextMessage = messagetext?.trim().length > 0;
        const hasPatientId = docModel?.Patients?.[0] != null;
        const patientId = hasPatientId ? docModel.Patients[0].Id : "";
        const hasUnsentMsg = hasTextMessage && hasPatientId;

        // create new message if user has entered text in message textbox
        if (hasUnsentMsg) {
            portalMessages.push(SharedItemMessage.createNewSharedItemMessage(messagetext, priority));
        }

        const sharedItem = new SharedItem({
            SharedItemId: sharedItemId,
            Subject: subject,
            SharedItemType: PortalLinkedItemType.Document,
            PatientContactId: patientId,
            Messages: portalMessages,
            CanPatientReplyWithMessage: canPatientReplyWithMessage
        });

        store.DocumentActions.SendDocumentToPortal(
            objectId,
            sharedItem,
            includeDocument,
            doc.hasBeenModifiedSinceLastShared()
        );

        docSyncModel = docSyncModel.set("IsSending", true);
        store.DocumentsModel = store.DocumentsModel.set("PortalLinkedItemModel", docSyncModel);
    }

    private onPortalRevokedClicked(request: LocalEvent, store: DocumentsStore): void {
        const model = store.DocumentsModel.get("PortalLinkedItemModel");
        const sharedItemId: string = model.get("LinkedItemModel").getSharedItemId();

        store.DocumentActions.RevokeDocumentToPortal(sharedItemId);
    }

    private trailClick(request: LocalEvent, store: DocumentsStore): void {
        const attachedCommunications = request.Get<AttachedCommunication[]>("attachedCommunications");
        store.DocumentsModel = store.DocumentsModel
            .set("AttachedCommunicationsPopupList", attachedCommunications);

        const eventTarget = request.Get<EventTarget>("eventTarget");
        const eventY = request.Get<number>("eventY");
        const eventX = request.Get<number>("eventX");

        if (eventTarget && eventY && eventX) {
            this.openTaskModal(eventTarget, eventY, eventX, store);
        }
    }

    private openTaskModal(eventTarget: EventTarget, eventY: number, eventX: number, store: DocumentsStore): void {
        $(eventTarget).closest("tr").parent().children().removeClass("active"); // deselect all table entries.
        $(eventTarget).closest("tr").addClass("active");

        let footerTopPosition = 0;
        if ($(".table-pager-footer").length > 0) { footerTopPosition = $(".table-pager-footer").position().top; }
        footerTopPosition = footerTopPosition - (eventY);

        $("#attached_communication_documentsearch").css("visibility", "hidden");

        window.setTimeout(() => {
            $("#attached_communication_documentsearch").removeClass("displaced");
            let attachedCommunicationWindowHeight = $("#attached_communication_documentsearch").height();
            $("#attached_communication_documentsearch").find("textarea").focus();
            if (attachedCommunicationWindowHeight > footerTopPosition) {
                let attachedCommunicationPositionTop = $("#attached_communication_documentsearch").position().top;
                let minimalTopPosition = attachedCommunicationPositionTop - attachedCommunicationWindowHeight;
                if (minimalTopPosition < 52)
                    minimalTopPosition = 52;
                $("#attached_communication_documentsearch").css("top", minimalTopPosition + "px");
                $("#attached_communication_documentsearch").addClass("displaced");
            }
            $("#attached_communication_documentsearch").css("visibility", "visible");
        }, 30);

        store.DocumentsModel = store.DocumentsModel
            .set("AttachedCommunicationsPopupX", eventX - 600)
            .set("AttachedCommunicationsPopupY", eventY)
            .set("AttachedCommunicationsPopupOpen", true);
    }

    private attachedCommunicationClick(request: LocalEvent, store: DocumentsStore): void {
        let attachedCommunication = request.Get<AttachedCommunication>("attachedCommunication");

        window.setTimeout(() => {
            sessionStore.dispatch(
                LocalOperations.Misc.RedirectToTab,
                {
                    value: attachedCommunication.PatientContactId,
                    currentLocation: RouterHistory.location,
                    taskId: attachedCommunication.IndividualMessageId,
                    target: RedirectTo.Patient
                }
            );
        }, 100);

        this.attachedCommunicationClose(request, store);
    }

    private attachedCommunicationClose(request: LocalEvent, store: DocumentsStore): void {
        store.DocumentsModel = store.DocumentsModel.set("AttachedCommunicationsPopupOpen", false);
        store.EmitChangeToListeners();
    }

    private showOnlySelectedSites(request: LocalEvent, store: DocumentsStore): void {
        store.DocumentsModel = store.DocumentsModel.set("SelectAllSitesChecked", false);
        store.DocumentsModel = store.DocumentsModel.set("ShowOnlySelectedSites", true);
        store.EmitChangeToListeners();
    }

    private resetCache(request: LocalEvent, store: DocumentsStore): void {
        const today = moment().toDate();
        store.DocumentsModel = store.DocumentsModel.set("DateTo", today);
        cacheStore.DocumentsState = cacheStore.DocumentsState.set("DateTo", today);
    }

    // HRM_CLEANUP: Delete this method when Ontario MD certification is complete.
    private refreshDocumentClasses(request: LocalEvent, store: DocumentsStore): void {
        const previousCheckboxes = store.DocumentsModel?.get("Classes")?.toArray() ?? [];
        const updatedCheckboxes = DocumentTreeClassControl.GetDocumentClassesAsCheckboxUIModel(CheckboxTarget.Myle_Documents_Classes);

        const previousCheckedState = new Map<string, boolean>();
        previousCheckboxes.forEach(item => previousCheckedState.set(item.Id, item.Checked));

        const alwaysCheckedClasses = [
            "Diagnostic Imaging Report",
            "Diagnostic Test Report",
            "Other Letter",
            "Consultant Report",
            "Medical Records Report",
            "Cardio Respiratory Report"
        ];

        updatedCheckboxes.forEach(option => {
            if (alwaysCheckedClasses.some(className => option.Name.includes(className))) {
                option.Checked = true;
            }
            else {
                const previousChecked = previousCheckedState.get(option.Id);
                if (previousChecked != null)
                    option.Checked = previousChecked;
            }
        });

        const currentDocumentClasses = store.DocumentsModel?.get("DocumentClasses");
        const updatedDocumentClasses = DocumentTreeClassControl.GetDocumentClasses(currentDocumentClasses);

        store.DocumentsModel = store.DocumentsModel
            .set("Classes", Immutable.List(updatedCheckboxes))
            .set("DocumentClasses", updatedDocumentClasses);
        store.EmitChangeToListeners();
    }
}

export { DocumentSearchHandler };
